<template>
  <ModalVue
    :modalActive="isThisModalVisible"
    @close="close"
    :hasHeaderBorder="true"
    :hasFooterBorder="true"
  >
    <Spinner
      v-if="isLoadingSubmit"
      color="primary"
      size="5rem"
      width="0.5rem"
      class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
    />

    <template #header>
      <ActionModalHeader :station-details="stationDetails" :title="modalTitle" />
    </template>

    <div
      v-if="isWizard"
      class="pb-5 mb-6 border-b border-gray-200"
      data-testid="invite-wizard-stepper"
    >
      <ScheduleStepper :steps="WIZARD_STEPS" :current="stepperCurrent" hide-labels />
    </div>

    <InviteFillModeStep
      v-if="wizardView === 'FILL_MODE'"
      v-model:fill-mode="fillMode"
      v-model:invitees="invitees"
      v-model:replacement="replacement"
      :doctors="doctors"
      :main-doctor-id="selectedDoctor"
      :has-current-doctor="hasCurrentDoctor"
      :max-invitees="invitePreview?.maxInvitees ?? DEFAULT_MAX_INVITEES"
      :disabled="isLoadingPreviewStep"
    />

    <InviteSentStep
      v-else-if="wizardView === 'SENT' && createdInvite"
      :links="createdInvite.links"
      :doctor-name="doctorNameById"
      :deadline-text="formatInviteDeadline(createdInvite.expiresAt, unitTimezone)"
    />

    <div v-else-if="wizardView === 'FORM'" class="grid grid-cols-1 gap-6">
      <CoverageModeSelector
        v-if="scheduleSupports"
        v-model="coverageMode"
        :allow-unidentified="allowUnidentified"
        :unidentified-locked="unidentifiedLocked"
        :can-open-schedule-settings="canOpenScheduleSettings"
        @open-schedule-settings="openScheduleSettings(close)"
      />
      <CoverageResponsibleField
        v-if="coverageMode === CoverageMode.UNIDENTIFIED"
        v-model:responsible-id="coverageResponsibleId"
        v-model:note="coverageNote"
        :health-unit-id="props.stationDetails?.schedule?.healthUnit?.id ?? null"
        :health-unit-name="props.stationDetails?.schedule?.healthUnit?.name ?? null"
        :fallback-name="originCoverage.responsibleName"
        @update:responsible-name="selectedResponsibleName = $event"
      />

      <template v-if="isDoctorTarget">
        <v-autocomplete
          :model-value="selectedDoctor"
          @update:model-value="onDoctorSelected"
          :items="doctors"
          label="Médico"
          variant="outlined"
          color="primary20"
          density="comfortable"
          item-title="title"
          item-value="value"
          :loading="isLoadingDoctors"
          @update:search="handleSearchUpdate"
          clearable
          hide-details
          :no-data-text="isLoadingDoctors ? 'Carregando...' : 'Nenhum médico encontrado'"
          data-testid="management-doctor-select"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item v-bind="props" :title="item?.raw?.title" :subtitle="item?.raw?.subtitle">
              <template v-if="eligibilityByDoctor[item?.raw?.value]" #append>
                <EligibilityChip :result="eligibilityByDoctor[item?.raw?.value]" />
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>

        <div class="flex flex-col md:flex-row gap-4">
          <v-autocomplete
            v-model="selectedFormation"
            :items="formations"
            label="Formação"
            variant="outlined"
            color="primary20"
            density="comfortable"
            item-title="title"
            item-value="value"
            :loading="isLoadingFormations"
            clearable
            hide-details
            no-data-text="Nenhuma formação encontrada"
          />

          <v-autocomplete
            v-model="selectedTeam"
            :items="teams"
            label="Equipe"
            variant="outlined"
            color="primary20"
            density="comfortable"
            item-title="title"
            item-value="value"
            :loading="isLoading"
            clearable
            hide-details
            no-data-text="Nenhuma equipe encontrada"
          />
        </div>

        <v-autocomplete
          v-model="selectedAllocator"
          :items="allocators"
          label="Médico escalado por"
          variant="outlined"
          color="primary20"
          density="comfortable"
          item-title="title"
          item-value="value"
          :loading="isLoadingAllocators"
          clearable
          hide-details
          no-data-text="Nenhum médico encontrado"
        />
      </template>

      <div v-if="coverageMode !== CoverageMode.OPEN">
        <p class="text-left text-gray-500 mb-4">Periodicidade</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6">
          <SelectableCard
            v-for="opt in periodicity"
            :key="opt.value"
            :title="opt.title"
            :description="opt.description"
            :selected="selectedPeriodicity === opt.value"
            @click="selectedPeriodicity = opt.value"
            class="!flex"
          />
        </div>
      </div>

      <div class="mt-2 flex flex-col gap-4">
        <v-autocomplete
          v-model="selectedRecurrence"
          :items="recurrence"
          label="Recorrência da alocação"
          variant="outlined"
          color="primary20"
          density="comfortable"
          item-title="title"
          item-value="value"
          :loading="isLoading"
          clearable
          hide-details
          no-data-text="Nenhuma recorrência encontrada"
        />

        <p class="text-left text-sm text-[#68688D] -mt-3">{{ computedRecurrenceDescription }}</p>

        <OptionSelect
          v-if="selectedRecurrence === 'abcde'"
          label="Selecione as semanas do mês a serem fixadas"
          :options="weekOptions"
          v-model="selectedWeeks"
          multiple
        />

        <OptionSelect
          v-if="selectedRecurrence !== 'unique'"
          label="Selecione os dias da semana a serem alocados"
          :options="dayOptions"
          v-model="selectedDay"
          multiple
        />

        <v-row v-if="selectedRecurrence !== 'unique'">
          <div class="flex w-full gap-4">
            <div :class="selectedRepetition === 'personalized' ? 'w-3/4' : 'w-full'">
              <v-autocomplete
                v-model="selectedRepetition"
                :items="repetition"
                label="Se repete até"
                variant="outlined"
                color="primary20"
                density="comfortable"
                item-title="title"
                item-value="value"
                :loading="isLoading"
                hide-details
              />
            </div>

            <div v-if="selectedRepetition === 'personalized'" class="w-1/4">
              <DatePicker
                v-model="startDate"
                :max-date="
                  props.stationDetails?.schedule.endDate
                    ? new Date(props.stationDetails?.schedule.endDate)
                    : null
                "
                :initial-page="initialPage"
                locale="pt-BR"
                :masks="{ input: 'DD/MM/YYYY' }"
              >
                <template #default="{ inputValue, inputEvents }">
                  <v-text-field
                    label="Data final"
                    :model-value="inputValue"
                    v-on="inputEvents"
                    color="primary20"
                    variant="outlined"
                    density="comfortable"
                    :prepend-inner-icon="mdiCalendar"
                    readonly
                    hide-details
                  />
                </template>
              </DatePicker>
            </div>
          </div>
        </v-row>
      </div>
    </div>

    <div v-else-if="wizardView === 'PREVIEW'">
      <div v-if="totalShiftsCount > 0" class="flex flex-col gap-4">
        <p class="text-sm text-left text-textPrimary" data-testid="management-doctor-summary">
          {{ previewSummaryText }}
        </p>

        <InvitePreviewNotices
          v-if="isWizard"
          :fill-mode="fillMode"
          :preview="invitePreview"
          :invitee-names="inviteeNames"
          :replacement="replacement"
          :has-current-doctor="hasCurrentDoctor"
          :pending-commitments="pendingCommitments"
          :pending-check-failed="pendingCommitmentsFailed"
          :doctor-name="doctorNameById"
          :timezone="unitTimezone"
          :doctor-count="inviteDoctorCount"
        />

        <!-- O convite não mexe na cobertura (D88): só a direta mostra. -->
        <CoveragePreviewSummary
          v-if="coveragePlan.hasContract && !isInviteFlow"
          :plan="coveragePlan"
          :target-doctor-name="isDoctorTarget ? computedDoctorDetails.name : null"
        />

        <div class="flex flex-col md:flex-row gap-4">
          <CardDutyWithDoctor
            v-if="isDoctorTarget"
            :formation="computedDoctorDetails.formation"
            :team="computedDoctorDetails.team"
            :dutyLabel="
              selectedPeriodicity === 'COBERTURA'
                ? 'Cobertura'
                : selectedPeriodicity === 'FIXO'
                  ? 'Fixo'
                  : ''
            "
            :doctorName="computedDoctorDetails.name"
            :doctorCrm="computedDoctorDetails.crm"
            :active="true"
            :layout="'simple'"
            :npsScore="stationDetails?.doctorNpsScore?.npsScore"
            :avatarUrl="stationDetails?.doctorStation?.doctor?.user?.avatarUrl"
          />

          <div class="flex flex-col text-textPrimary">
            <p
              v-if="shouldShowInitialStationWarning"
              class="text-sm bg-warning/20 border border-warning rounded-md px-4 py-1 mb-4"
            >
              A alocação configurada não altera o plantão selecionado inicialmente<span
                class="bg-gray-200 rounded px-2 border border-gray-500 ml-4"
                >#{{ stationDetails?.doctorStation?.id }}</span
              >
            </p>
            <p
              v-if="hasConflictingStations"
              class="text-sm bg-negative/10 border border-negative rounded-md px-4 py-2 mb-4 flex items-center gap-2 text-[#DA1E28]"
            >
              <v-icon :icon="mdiAlertCircleOutline" :color="'#DA1E28'" class="w-5 h-5 shrink-0" />
              <span class="text-black"
                >Uma ou mais vagas selecionadas têm horários sobrepostos, ocorrendo conflito de
                alocação</span
              >
            </p>

            <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-left text-sm md:text-base">
              <div class="flex flex-col">
                <span class="font-semibold">Empresa</span>
                <span>{{ stationDetails?.schedule?.healthUnit?.company?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Escala</span>
                <span>{{ stationDetails?.schedule?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Unidade</span>
                <span>{{ stationDetails?.schedule?.healthUnit?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Posição</span>
                <span>{{ stationDetails?.position?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Turno</span>
                <span>{{ stationDetails?.shift?.name || 'N/A' }}</span>
              </div>
              <div class="flex flex-col">
                <span class="font-semibold">Fuso horário</span>
                <span>{{ stationDetails?.timezone || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>

        <ManagementDoctorTable
          :headers="previewHeaders"
          :items="limitedFormattedShifts"
          expandable
          @expand="handleExpandTable"
        />
        <!-- No convite, só o "Sai agora" pede motivo (D104); os campos vêm vazios nos outros. -->
        <div v-if="reasonFields.length" class="flex flex-col gap-4 mt-4">
          <ChangeReasonField
            v-for="(field, index) in reasonFields"
            :key="field.kind"
            v-model="reasonValues[field.kind]"
            v-bind="field"
            :show-errors="showReasonErrors"
            :scope-note="index === reasonFields.length - 1 ? reasonScopeNote : ''"
          />
        </div>

        <ConsecutiveHoursBlockAlert :violations="consecutiveHoursViolations" :bulk="true" />
      </div>
      <div v-else class="flex flex-col items-center justify-center text-center py-10">
        <v-icon class="w-24 h-24 mb-4" :icon="mdiAlertCircleOutline" :color="'#DA1E28'" />
        <p class="text-base text-gray-600">
          Nenhum plantão encontrado com os parâmetros selecionados.
        </p>
      </div>
    </div>

    <template #footer>
      <div class="p-4">
        <template v-if="wizardView === 'FORM'">
          <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
            <OutlineButton height="48px" color="grey" @click="close" class="w-full md:w-auto">
              Cancelar
            </OutlineButton>
            <FilledButton
              v-if="isWizard"
              height="48px"
              color="primary"
              @click="goToFillMode"
              :disabled="!isViewShiftsButtonEnabled"
              class="w-full md:w-auto"
              data-testid="invite-wizard-continue-button"
            >
              Continuar
            </FilledButton>
            <FilledButton
              v-else
              height="48px"
              color="primary"
              @click="handleViewDoctorStations()"
              :loading="isLoadingShifts"
              :disabled="!isViewShiftsButtonEnabled"
              class="w-full md:w-auto"
            >
              Ver plantões
            </FilledButton>
          </div>
        </template>

        <template v-else-if="wizardView === 'FILL_MODE'">
          <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
            <OutlineButton
              height="48px"
              color="grey"
              @click="backToForm"
              :disabled="isLoadingPreviewStep"
              class="w-full md:w-auto"
              data-testid="invite-wizard-back-button"
            >
              Voltar
            </OutlineButton>
            <FilledButton
              height="48px"
              color="primary"
              @click="handleWizardViewStations"
              :loading="isLoadingPreviewStep"
              class="w-full md:w-auto"
              data-testid="invite-wizard-view-stations-button"
            >
              Ver plantões
            </FilledButton>
          </div>
        </template>

        <template v-else-if="wizardView === 'SENT'">
          <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
            <OutlineButton
              height="48px"
              color="primary"
              @click="viewInvite"
              class="w-full md:w-auto"
              data-testid="invite-wizard-view-invite-button"
            >
              Ver detalhes do convite
            </OutlineButton>
            <FilledButton
              height="48px"
              color="primary"
              @click="close"
              class="w-full md:w-auto"
              data-testid="invite-wizard-close-button"
            >
              Fechar
            </FilledButton>
          </div>
        </template>

        <template v-else-if="wizardView === 'PREVIEW'">
          <template v-if="totalShiftsCount > 0">
            <div class="flex flex-col-reverse md:flex-row gap-3 justify-between w-full">
              <OutlineButton
                height="48px"
                color="grey"
                @click="backFromPreview"
                class="w-full md:w-auto"
              >
                Voltar
              </OutlineButton>
              <FilledButton
                height="48px"
                color="primary"
                @click="handlePreviewSubmit"
                :loading="isLoadingSubmit"
                :disabled="hasConsecutiveHoursViolations || isSubmitBlocked || inviteBlocked"
                class="w-full md:w-auto"
                data-testid="management-doctor-submit"
              >
                {{ previewSubmitLabel }}
              </FilledButton>
            </div>
          </template>
          <template v-else>
            <div class="flex justify-center w-full">
              <FilledButton
                height="48px"
                color="primary"
                @click="backFromPreview"
                class="w-full md:w-auto"
              >
                Voltar
              </FilledButton>
            </div>
          </template>
        </template>
      </div>
    </template>
  </ModalVue>

  <ExpandedTableModal
    :showModal="isExpandedTableModalOpen"
    :headers="previewHeaders"
    :items="formattedShifts"
    :mode="mode === 'change' ? 'change' : 'allocate'"
    :title="modalTitle"
    :conflict-count="conflictShiftsCount"
    :open-count="coveragePlan.hasContract ? coveragePlan.counts.OPEN : openShiftsCount"
    :closed-count="coveragePlan.hasContract ? coveragePlan.counts.DOCTOR : closedShiftsCount"
    :unidentified-count="
      coveragePlan.hasContract ? coveragePlan.counts.UNIDENTIFIED : unregisteredShiftsCount
    "
    :blocked-count="coveragePlan.hasContract ? coveragePlan.counts.BLOCKED : null"
    :total-count="totalShiftsCount"
    @close="handleCloseExpandedTable"
  />

  <CoverageTransitionConfirmModal
    :show-modal="isTransitionModalOpen"
    :summary="transitionSummary"
    :context-label="transitionContextLabel"
    :responsible-name="selectedResponsibleName"
    :loading="isLoadingSubmit"
    @confirm="handleTransitionConfirm"
    @cancel="isTransitionModalOpen = false"
  />

  <!-- v-if: o aviso (com as ilustrações) só existe no DOM enquanto aberto. -->
  <PlaceholderDoctorBlockedModal
    v-if="placeholderBlocked.isOpen.value"
    v-model="placeholderBlocked.isOpen.value"
    :doctor-name="placeholderBlocked.doctorName.value"
    :allow-unidentified="allowUnidentified"
    @use-unidentified="onUseUnidentified"
  />

  <!-- Qualificação exigida (SPEC §4): 422 da gravação → bloqueio ou confirmação da pré-alocação. -->
  <QualificationBlockedDialog
    v-if="qualificationGuard.blocked.open"
    v-model="qualificationGuard.blocked.open"
    :doctor-name="qualificationGuard.blocked.doctorName"
    :missing="qualificationGuard.blocked.missing"
  />
  <PreAllocationConfirmDialog
    v-if="qualificationGuard.preAllocation.open"
    :model-value="qualificationGuard.preAllocation.open"
    :doctor-name="qualificationGuard.preAllocation.doctorName"
    :missing="qualificationGuard.preAllocation.missing"
    :station-count="preAllocationStationCount"
    @confirm="qualificationGuard.confirmPreAllocation()"
    @cancel="qualificationGuard.cancelPreAllocation()"
  />
</template>

<script lang="ts" setup>
import { ref, computed, PropType, watch, toRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import { mdiAlertCircleOutline, mdiCalendar } from '@mdi/js'
import { format, endOfDay } from 'date-fns'

import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import DsPaymentFormationService from '@/Network/Services/DsPaymentFormationService'
import ScheduleUnitService from '@/Network/Services/ScheduleUnitService'
import StationService from '@/Network/Services/StationService'
import { ShiftDetails, StationFormSchedule } from '@/Network/Types/Responses/StationResponses'
import {
  ListStationsFromScheduleRequest,
  PatchStationsFromSchedulePayload,
  StationTypes
} from '@/Network/Types/Requests/StationRequests'

import ModalVue from '@/components/designSystem/modals/Modal.vue'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import CardDutyWithDoctor from '@/components/designSystem/Cards/CardDutyWithDoctor.vue'
import ManagementDoctorTable from '../../components/ManagementDoctorTable.vue'
import ExpandedTableModal from '../../components/ExpandedTableModal.vue'
import OptionSelect from '@/components/designSystem/Select/OptionSelect.vue'
import Spinner from '@/components/others/Spinner.vue'

import {
  dayMapping,
  dayOptions,
  periodicity,
  recurrence,
  recurrenceDescriptions,
  repetition,
  tableHeaders,
  weekMapping,
  weekOptions
} from '../utils/data'
import { useStationStore } from '@/pinia-store/useStationStore'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import { useExpandedTable, useInitialPage, useSummaryText } from '../utils/modalUtils'
import ActionModalHeader from '../../components/ActionModalHeader.vue'
import ConsecutiveHoursBlockAlert from '@/components/designSystem/Alerts/ConsecutiveHoursBlockAlert.vue'
import { useConsecutiveHoursCheck } from '@/hooks/useConsecutiveHoursCheck'
import type { ConsecutiveHoursCheckCandidate } from '@/Network/Types/Requests/StationRequests'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'
import CoverageModeSelector from '@/components/Coverage/CoverageModeSelector.vue'
import { useUnidentifiedCoverageLock } from '@/components/Coverage/useUnidentifiedCoverageLock'
import CoverageResponsibleField from '@/components/Coverage/CoverageResponsibleField.vue'
import CoverageTransitionConfirmModal from '@/components/Coverage/CoverageTransitionConfirmModal.vue'
import CoveragePreviewSummary from '../../components/CoveragePreviewSummary.vue'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import {
  canMarkUnidentified,
  getStationCoverageInfo,
  scheduleSupportsCoverage
} from '@/utils/coverage'
import { getCoverageErrorMessage } from '@/utils/coverageErrors'
import PlaceholderDoctorBlockedModal from '@/components/Coverage/PlaceholderDoctorBlockedModal.vue'
import { usePlaceholderDoctorBlocked } from '@/composables/usePlaceholderDoctorBlocked'
import EligibilityChip from '@/components/Qualification/EligibilityChip.vue'
import PreAllocationConfirmDialog from '@/components/Qualification/PreAllocationConfirmDialog.vue'
import QualificationBlockedDialog from '@/components/Qualification/QualificationBlockedDialog.vue'
import { useEligibilityPrecheck } from '@/composables/useEligibilityPrecheck'
import { useQualificationAllocationGuard } from '@/composables/useQualificationAllocationGuard'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import type { EligibilityResult } from '@/types/qualification-eligibility.types'
import ScheduleStepper from '@/components/ManageScale/Schedule/ScheduleStepper.vue'
import type {
  AllocationInvitePreview,
  CreatedAllocationInvite,
  PendingCommitment
} from '@/Network/Types/Responses/AllocationInviteResponses'
import {
  findDoctorTitle,
  focusCoverageResponsibleSelect,
  focusFieldBySelector,
  isBlockedPlaceholderDoctor,
  reportPreventivePlaceholderAttempt
} from '@/utils/placeholderDoctor'
import ChangeReasonField from '@/components/ChangeReason/ChangeReasonField.vue'
import { useChangeReasonForm } from '@/composables/useChangeReasonForm'
import {
  CHANGE_REASON_LABELS,
  CHANGE_REASON_TITLES,
  EXTERNAL_REASON_OPTIONS,
  changeReasonScopeNote,
  isEarlyWithdrawalWindow,
  removalReasonOptions,
  stationStartsAt
} from '@/utils/changeReasons'
import {
  buildCoverageOperations,
  buildCoveragePatchStations,
  buildCoveragePlan,
  buildTransitionSummary,
  coverageTableHeaders,
  formatLegacyAvailability,
  needsTransitionConfirmation,
  toPreviewTableRow
} from '../utils/coverageAllocation'
import { requiredReasonKinds } from '../utils/changeReasonPlan'
import InviteFillModeStep from './InviteFillModeStep.vue'
import InviteSentStep from './InviteSentStep.vue'
import InvitePreviewNotices from './InvitePreviewNotices.vue'
import AllocationInviteService from '@/Network/Services/AllocationInviteService'
import type { AllocationInvitePreviewRequest } from '@/Network/Types/Requests/AllocationInviteRequests'
import {
  buildInvitePreviewRequest,
  buildInviteRequest,
  exceedsInviteStationLimit,
  formatInviteDeadline,
  INVITE_STATION_LIMIT_MESSAGE,
  inviteRemovesCurrentDoctorNow,
  inviteSummaryText,
  pluralizeStations,
  resolveInviteMode,
  resolveUnitTimezone,
  type FillMode,
  type ReplacementChoice
} from './inviteWizard'

let searchDebounceTimer: number | undefined = undefined
// Último termo do autocomplete de médico: `doctors` fica filtrado por ele no servidor.
let lastDoctorSearch = ''

const props = defineProps({
  showModal: {
    type: Boolean,
    required: true
  },
  stationDetails: {
    type: Object as PropType<ShiftDetails | null>,
    default: () => null
  },
  mode: {
    type: String as PropType<'allocate' | 'change'>,
    required: true
  },
  // "Editar responsável" abre direto em "Ocupado sem identificação"; null = "Médico".
  initialCoverage: {
    type: String as PropType<CoverageMode | null>,
    default: null
  }
})

const emit = defineEmits([
  'closeModal',
  'allocationUpdated',
  /** `[inviteId: number]` — a WEB-2 abre o painel do convite a partir dele. */
  'inviteCreated'
])

const toast = useToast()
const { notifyError, notifyErrorMessage } = useErrorToast()
const doctorStore = useDoctorStore()
const stationService = new StationService()
const { listDoctorsCompany } = storeToRefs(doctorStore)
const stationStore = useStationStore()

const isThisModalVisible = ref(props.showModal)
const isExpandedTableModalOpen = ref(false)
const showShiftList = ref(false)
const isLoadingDoctors = ref(false)
const isLoadingFormations = ref(false)
const isLoadingAllocators = ref(false)
const isLoadingShifts = ref(false)
const isLoadingSubmit = ref(false)

// `value` = Doctor.id (alocação); `userId` = User.id (pré-checagem de qualificação, R6/A8).
const doctors = ref<{ title: string; value: any; subtitle: string; userId: number | null }[]>([])
const formations = ref<{ title: string; value: string | number }[]>([])
const teams = ref([
  { title: 'Interno', value: 'internal' },
  { title: 'Externo', value: 'external' }
])
const allocators = ref<{ title: string; value: string | number }[]>([])
const shifts = ref<StationFormSchedule[]>([])
const totalShiftsCount = ref(0)
const openShiftsCount = ref(0)
const closedShiftsCount = ref(0)
// `countUnregistered` do BE: ocupado sem identificação saiu de `countOpen` (null = BE antigo).
const unregisteredShiftsCount = ref<number | null>(null)
const conflictShiftsCount = ref(0)

const selectedDoctor = ref<string | null>(null)
const selectedFormation = ref<number | null>(null)
const selectedTeam = ref<string | null>(null)
const selectedAllocator = ref<number | null>(null)
const selectedPeriodicity = ref<string | null>('COBERTURA')
const selectedRecurrence = ref('unique')
const selectedRepetition = ref('end_scale')
const selectedWeeks = ref(['A', 'C'])
const selectedDay = ref(['seg'])
const startDate = ref()

const coverageMode = ref<CoverageMode>(CoverageMode.DOCTOR)
const coverageResponsibleId = ref<number | null>(null)
const coverageNote = ref('')
const selectedResponsibleName = ref<string | null>(null)
const isTransitionModalOpen = ref(false)

// Aviso do médico "coringa" (PLACEHOLDER_DOCTOR_BLOCKED): 422 do BE ou bloqueio preventivo.
const placeholderBlocked = usePlaceholderDoctorBlocked()
// Aviso aberto pela seleção no autocomplete: ao fechar ("Entendi"/ESC), o foco volta ao médico.
let focusDoctorOnClose = false
watch(placeholderBlocked.isOpen, (open) => {
  if (open || !focusDoctorOnClose) return
  focusDoctorOnClose = false
  void focusFieldBySelector('[data-testid="management-doctor-select"]')
})

// Seleção de médico: um "coringa" bloqueado nesta escala abre o aviso na hora e NÃO é aceito
// (o autocomplete é controlado, então o valor volta ao anterior).
function onDoctorSelected(value: string | null) {
  const schedule = props.stationDetails?.schedule
  if (isBlockedPlaceholderDoctor(schedule, value)) {
    placeholderBlocked.open(findDoctorTitle(doctors.value, value))
    focusDoctorOnClose = true
    reportPreventivePlaceholderAttempt(
      (payload) => stationService.reportPlaceholderAllocationAttempt(payload),
      {
        doctorId: Number(value),
        scheduleId: Number(schedule?.id),
        stationIds: props.stationDetails?.id ? [props.stationDetails.id] : undefined
      }
    )
    return
  }
  selectedDoctor.value = value
}

// Qualificação exigida (SPEC §4): 422 → bloqueio (sem liberação manual) ou pré-alocação confirmada.
const qualificationGuard = useQualificationAllocationGuard()
const { flags } = useFeatureFlags()
// Chips na lista de médicos: pré-checagem do plantão aberto (só UX; a gravação revalida).
const eligibilityPrecheck = useEligibilityPrecheck(
  () => ({
    scheduleId: props.stationDetails?.schedule?.id ?? null,
    stationIds: props.stationDetails?.id ? [props.stationDetails.id] : [],
    userIds: doctors.value.flatMap((doctor) => (doctor.userId ? [doctor.userId] : []))
  }),
  { isEnabled: () => flags.enabledProfessionalDocuments && props.showModal }
)
const eligibilityByDoctor = computed<Record<string, EligibilityResult>>(() =>
  Object.fromEntries(
    doctors.value.flatMap((doctor) => {
      const result = doctor.userId ? eligibilityPrecheck.resultFor(doctor.userId) : null
      return result ? [[String(doctor.value), result]] : []
    })
  )
)

// Lista do autocomplete a partir da store; o User.id vem do registro bruto (`doctorsCompany`).
function syncDoctorOptions(): void {
  const userIdByDoctor = new Map(
    (doctorStore.doctorsCompany ?? []).map((item) => [
      String(item.doctor.id),
      item.doctor.user?.id ?? null
    ])
  )
  doctors.value = listDoctorsCompany.value.map((doc) => ({
    title: doc.title,
    value: doc.value,
    subtitle: doc.subtitle,
    userId: userIdByDoctor.get(String(doc.value)) ?? null
  }))
}

// "Usar Ocupado sem identificação": volta ao formulário já em UNIDENTIFIED, sem médico.
function onUseUnidentified() {
  // O primário leva o foco ao responsável (o campo de médico some com UNIDENTIFIED).
  focusDoctorOnClose = false
  coverageMode.value = CoverageMode.UNIDENTIFIED
  selectedDoctor.value = null
  showShiftList.value = false
  // Assistente de convite: volta ao passo 1 (UNIDENTIFIED não tem passos, mas o índice não pode
  // ficar no resumo se o usuário voltar para "Médico").
  wizardStep.value = 0
  shifts.value = []
  void focusCoverageResponsibleSelect()
}

const scheduleSupports = computed(() => scheduleSupportsCoverage(props.stationDetails?.schedule))
const allowUnidentified = computed(() => canMarkUnidentified(props.stationDetails?.schedule))
const { unidentifiedLocked, canOpenScheduleSettings, openScheduleSettings } =
  useUnidentifiedCoverageLock(() => props.stationDetails?.schedule)
const originCoverage = computed(() => getStationCoverageInfo(props.stationDetails))
const isDoctorTarget = computed(() => coverageMode.value === CoverageMode.DOCTOR)
const coveragePlan = computed(() => buildCoveragePlan(shifts.value, coverageMode.value))
const transitionSummary = computed(() => buildTransitionSummary(coveragePlan.value))
const previewHeaders = computed(() =>
  coveragePlan.value.hasContract ? coverageTableHeaders : tableHeaders
)

// ---- Assistente de convite (acceptance-terms WEB-1, D27/D31; flag D55/D91) ----
// Declarado depois de `isDoctorTarget`/`selectedDoctor`/`showShiftList`, de que depende.
type WizardView = 'FORM' | 'FILL_MODE' | 'PREVIEW' | 'SENT'
/** 0 = formulário, 1 = forma de preencher, 2 = resumo, 3 = convite enviado. */
const wizardStep = ref(0)
const fillMode = ref<FillMode>('INVITE')
const invitees = ref<string[]>([])
const replacement = ref<ReplacementChoice>('KEEP')
const invitePreview = ref<AllocationInvitePreview | null>(null)
const createdInvite = ref<CreatedAllocationInvite | null>(null)
const pendingCommitments = ref<PendingCommitment[]>([])
const pendingCommitmentsFailed = ref(false)
/** Request exata da prévia exibida: o envio parte dela, não do estado vivo do formulário. */
const previewRequest = ref<AllocationInvitePreviewRequest | null>(null)
/** Listagem + prévia/pendências do passo 3 (convite e direta) em andamento. */
const isLoadingPreviewStep = ref(false)
// Token das cargas do passo 3: limpar ou recarregar invalida a resposta que ainda está em voo.
let previewRequestSeq = 0
// Dono do `isLoadingPreviewStep`: uma carga obsoleta não desliga o indicador da carga nova.
let previewStepSeq = 0
const allocationInviteService = new AllocationInviteService()

// P5: o assistente só existe com a flag e com a cobertura "Médico".
const isWizard = computed(() => flags.enabledAllocationInvite === true && isDoctorTarget.value)
const isInviteFlow = computed(() => isWizard.value && fillMode.value === 'INVITE')
const hasCurrentDoctor = computed(() => !!props.stationDetails?.doctorStation?.doctor)
// `station.timezone` vem como offset (`-03:00`) e pode vir vazio: o helper normaliza.
const unitTimezone = computed(() => resolveUnitTimezone(props.stationDetails?.timezone))

const wizardView = computed<WizardView>(() => {
  if (wizardStep.value === 1) return 'FILL_MODE'
  if (wizardStep.value === 3) return 'SENT'
  return showShiftList.value ? 'PREVIEW' : 'FORM'
})
const stepperCurrent = computed(() => Math.min(wizardStep.value, 2))
const WIZARD_STEPS = ['Dados', 'Envio', 'Resumo']

// ---- Motivos de troca (station-change-reasons) ----
// Declarado depois do estado do assistente: o `watch` imediato do `useChangeReasonForm` já lê
// `isInviteFlow`/`replacement`/`invitePreview` na montagem.
/** Convite "Sai agora": o médico atual sai na criação, então o convite pede o motivo (D104). */
const isReplaceNowInvite = computed(
  () =>
    isInviteFlow.value &&
    inviteRemovesCurrentDoctorNow(resolveInviteMode(hasCurrentDoctor.value, replacement.value))
)
// No convite, o motivo vale para os plantões elegíveis da prévia (os demais ficam fora dele).
const reasonCount = computed(() =>
  isInviteFlow.value
    ? (invitePreview.value?.eligibleStationIds.length ?? 0)
    : coveragePlan.value.applicableRows.length
)
const reasonScopeNote = computed(() =>
  changeReasonScopeNote(reasonCount.value, isInviteFlow.value ? 'do convite' : 'da tabela')
)
const leavingDoctorName = computed(
  () => props.stationDetails?.doctorStation?.doctor?.user?.name ?? 'o médico'
)
/** Plantão clicado: referência do motivo de remoção no backend (decisões 41 e 45). */
const originStationId = computed(() => props.stationDetails?.id ?? null)
const removalReasonField = (offAppSwap: boolean) => ({
  kind: 'removal' as const,
  title: CHANGE_REASON_TITLES.removalOf(leavingDoctorName.value, reasonCount.value),
  label: CHANGE_REASON_LABELS.exit,
  options: removalReasonOptions({
    // `startHour` do GET /stations/:id é ISO com offset: o instante do plantão clicado.
    earlyWithdrawal: isEarlyWithdrawalWindow(stationStartsAt(props.stationDetails)),
    offAppSwap
  })
})
const {
  values: reasonValues,
  showErrors: showReasonErrors,
  fields: reasonFields,
  reset: resetReasons,
  validate: validateReasons,
  build: buildReasons
} = useChangeReasonForm(() => {
  // Convite: só o "Sai agora" remove alguém na criação (D104; "Fica até o aceite" e vaga não
  // pedem, D99). Ninguém entra na hora, então "Troca fora do aplicativo" não vale (o backend recusa).
  if (isInviteFlow.value) return isReplaceNowInvite.value ? [removalReasonField(false)] : []
  const toDoctor = isDoctorTarget.value
  const kinds = requiredReasonKinds(coveragePlan.value, {
    targetDoctorId: toDoctor && selectedDoctor.value ? Number(selectedDoctor.value) : null,
    externalTeam: toDoctor && selectedTeam.value === 'external'
  })
  return kinds.map((kind) =>
    kind === 'removal'
      ? // Decisões 19 e 52 (E6): há um médico saindo do plantão clicado, e ele e quem entra
        // são internos; o backend confere só o originStationId.
        removalReasonField(
          toDoctor &&
            selectedTeam.value === 'internal' &&
            !!props.stationDetails?.doctorStation?.doctor &&
            props.stationDetails?.doctorStation?.ourTeam === true
        )
      : {
          kind,
          title: CHANGE_REASON_TITLES.external(reasonCount.value),
          label: CHANGE_REASON_LABELS.external,
          options: EXTERNAL_REASON_OPTIONS
        }
  )
})

// `ALLOCATION_INVITE_MAX_INVITEES` do backend (D22). A prévia só roda no passo 3, então o
// passo 2 usa o padrão; a prévia e a criação revalidam o limite de qualquer forma.
const DEFAULT_MAX_INVITEES = 10

// A lista de convidados do passo 2 usa `doctors`, que herda o filtro da última busca do
// passo 1 ("Ana" → só as Anas). Recarrega a lista completa antes de avançar.
async function goToFillMode() {
  if (lastDoctorSearch.trim() !== '') {
    clearTimeout(searchDebounceTimer)
    lastDoctorSearch = ''
    await fetchDoctorsWithSearch('')
  }
  wizardStep.value = 1
}
function clearPreviewData() {
  previewRequestSeq++
  invitePreview.value = null
  previewRequest.value = null
  pendingCommitments.value = []
  pendingCommitmentsFailed.value = false
}
function backToForm() {
  wizardStep.value = 0
  showShiftList.value = false
  clearPreviewData()
}
function backFromPreview() {
  showShiftList.value = false
  wizardStep.value = isWizard.value ? 1 : 0
  clearPreviewData()
}
function resetWizard() {
  wizardStep.value = 0
  fillMode.value = 'INVITE'
  invitees.value = []
  replacement.value = 'KEEP'
  createdInvite.value = null
  clearPreviewData()
  // A carga em voo fica obsoleta pelo token; o indicador volta a ficar livre já.
  previewStepSeq++
  isLoadingPreviewStep.value = false
}

// A prévia (convite) e as pendências (direta) valem para a forma em que foram carregadas.
watch(fillMode, clearPreviewData)

// Trocar o médico do passo 1 é uma decisão nova: volta ao convite e limpa os convidados.
// (Um principal órfão força DIRECT no passo 2; sem este reset a escolha forçada ficaria presa.)
watch(selectedDoctor, () => {
  fillMode.value = 'INVITE'
  invitees.value = []
})

function doctorNameById(doctorId: number): string {
  return findDoctorTitle(doctors.value, String(doctorId)) ?? `Médico ${doctorId}`
}

async function handleWizardViewStations() {
  if (isLoadingPreviewStep.value) return
  const loadingOwner = ++previewStepSeq
  isLoadingPreviewStep.value = true
  try {
    await openPreviewStep(previewRequestSeq)
  } finally {
    // Carga obsoleta (o modal foi reaberto): o indicador pertence à carga nova, se houver.
    if (loadingOwner === previewStepSeq) isLoadingPreviewStep.value = false
  }
}

// Com contrato de cobertura vão só as linhas aplicáveis (as bloqueadas ficam de fora), como a
// alocação direta faz no envio.
function previewStationIds(): number[] {
  return coveragePlan.value.hasContract
    ? coveragePlan.value.applicableRows.map((row) => row.station.stationId)
    : shifts.value.map((s) => s.stationId)
}

async function openPreviewStep(seq: number) {
  // O modal fechou/reabriu (token novo) ou saiu do passo 2 durante a listagem: a resposta é
  // descartada, mesmo que o usuário já tenha voltado ao passo 2 por outro caminho.
  const isStale = () => seq !== previewRequestSeq || wizardStep.value !== 1
  await handleViewDoctorStations(isStale)
  if (isStale()) {
    if (wizardStep.value <= 1) showShiftList.value = false
    return
  }
  if (!showShiftList.value) return // falha ao listar: fica no passo 2 com o toast
  const stationIds = previewStationIds()
  // Sem plantão (ou todos bloqueados) não há o que pré-visualizar: o passo 3 já explica.
  if (totalShiftsCount.value === 0 || stationIds.length === 0) {
    wizardStep.value = 2
    return
  }
  const loaded = isInviteFlow.value
    ? await loadInvitePreview(stationIds)
    : await loadPendingCommitments(stationIds)
  if (!loaded || wizardStep.value !== 1) {
    showShiftList.value = false
    return
  }
  wizardStep.value = 2
}

/** Carrega a prévia do convite; false = falhou ou ficou obsoleta (o passo 3 não abre). */
async function loadInvitePreview(stationIds: number[]): Promise<boolean> {
  const scheduleId = Number(props.stationDetails?.schedule?.id)
  const mainDoctorId = Number(selectedDoctor.value)
  if (!Number.isFinite(scheduleId) || !mainDoctorId || stationIds.length === 0) {
    notifyErrorMessage('Preencha todos os campos e visualize os plantões antes de continuar.')
    return false
  }
  // D140: acima do teto o backend recusa a prévia (400); avisa antes e fica no passo 2.
  if (exceedsInviteStationLimit(stationIds)) {
    notifyErrorMessage(INVITE_STATION_LIMIT_MESSAGE)
    return false
  }
  const request = buildInvitePreviewRequest({
    scheduleId,
    stationIds,
    mainDoctorId,
    inviteeIds: invitees.value.map(Number),
    mode: resolveInviteMode(hasCurrentDoctor.value, replacement.value)
  })
  const requestId = ++previewRequestSeq
  previewRequest.value = request
  try {
    const preview = await allocationInviteService.preview(request)
    if (requestId !== previewRequestSeq || wizardStep.value !== 1) return false
    invitePreview.value = preview
    return true
  } catch (error) {
    if (requestId !== previewRequestSeq) return false
    invitePreview.value = null
    previewRequest.value = null
    notifyError(error, 'Não foi possível montar a prévia do convite')
    return false
  }
}

// Alocação direta com a flag ligada: avisa troca/convite pendente antes de confirmar (D15, D54).
async function loadPendingCommitments(stationIds: number[]): Promise<boolean> {
  const requestId = ++previewRequestSeq
  try {
    const pending = await allocationInviteService.pendingCommitments(stationIds)
    if (requestId !== previewRequestSeq) return false
    pendingCommitments.value = pending
    pendingCommitmentsFailed.value = false
  } catch (error) {
    if (requestId !== previewRequestSeq) return false
    // fallback intencional: o aviso é informativo e a cascata do backend cancela o pendente de
    // qualquer forma; a prévia segue com o aviso genérico de falha (visível ao usuário).
    pendingCommitments.value = []
    pendingCommitmentsFailed.value = true
    console.error(error)
  }
  return true
}

// Com prévia, a contagem é a da request enviada (fonte única); antes dela, o estado do passo 2.
const inviteDoctorCount = computed(
  () => previewRequest.value?.doctorIds.length ?? 1 + new Set(invitees.value).size
)
const eligibleCount = computed(() => invitePreview.value?.eligibleStationIds.length ?? 0)
const inviteBlocked = computed(() => isInviteFlow.value && eligibleCount.value === 0)
const inviteeNames = computed(() => invitees.value.map((id) => doctorNameById(Number(id))))
// Sem prévia no convite (todas as linhas bloqueadas pelo contrato) vale o texto da cobertura.
const previewSummaryText = computed(() =>
  isInviteFlow.value && invitePreview.value
    ? inviteSummaryText(inviteDoctorCount.value, eligibleCount.value)
    : summaryText.value
)
const previewSubmitLabel = computed(() =>
  isInviteFlow.value ? 'Enviar convite' : submitLabel.value
)

async function handlePreviewSubmit() {
  if (isInviteFlow.value) {
    await submitInvite()
    return
  }
  await submitAllocation()
}

async function submitInvite() {
  if (isLoadingSubmit.value) return
  const preview = invitePreview.value
  const request = previewRequest.value
  // Validação local, fora do try: não é erro de API.
  if (!preview || !request || preview.eligibleStationIds.length === 0 || !selectedAllocator.value) {
    notifyErrorMessage(
      'Preencha todos os campos e visualize os plantões antes de enviar o convite.'
    )
    return
  }
  // "Sai agora": sem o motivo da remoção o backend responde 400 (D104); os outros modos não têm campo.
  if (!validateReasons()) return
  // O envio repete a request da prévia exibida (médicos e forma); só os plantões viram os
  // elegíveis. `doctorIds` já vem com o principal primeiro e sem repetidos.
  const [mainDoctorId, ...inviteeIds] = request.doctorIds
  isLoadingSubmit.value = true
  let created = false
  try {
    createdInvite.value = await allocationInviteService.create(
      buildInviteRequest({
        scheduleId: request.scheduleId,
        stationIds: preview.eligibleStationIds,
        mainDoctorId,
        inviteeIds,
        mode: request.mode,
        formationId: selectedFormation.value,
        ourTeam: selectedTeam.value === 'internal',
        userSponsorId: selectedAllocator.value,
        // `buildInviteRequest` só manda o motivo no REPLACE_NOW.
        changeReasons: buildReasons({ originStationId: originStationId.value })
      })
    )
    // O modal fica aberto no passo 4; `allocationUpdated` só sai ao fechar (o pai refaz o detalhe
    // e o watcher deep deste modal rodaria `fetchData()` por baixo do passo 4).
    wizardStep.value = 3
    created = true
  } catch (error) {
    // A frase do backend (catálogo pt-BR) vem no ServiceError; o fallback só vale sem ela.
    console.error('Erro ao enviar o convite:', error)
    notifyError(error, 'Não foi possível enviar o convite')
  } finally {
    isLoadingSubmit.value = false
  }
  if (!created) return
  try {
    await stationStore.getListStations()
  } catch (error) {
    // fallback intencional: o convite já foi criado; a grade atualiza ao fechar (allocationUpdated)
    console.error(error)
  }
}

// WEB-2 abre o painel do convite a partir deste evento; aqui só fecha.
function viewInvite() {
  if (createdInvite.value) emit('inviteCreated', createdInvite.value.inviteId)
  close()
}

const modalTitle = computed(() => {
  if (isInviteFlow.value && wizardStep.value >= 1) return 'Convidar médico'
  if (originCoverage.value.status === CoverageStatus.UNIDENTIFIED) {
    return coverageMode.value === CoverageMode.UNIDENTIFIED
      ? 'Editar responsável'
      : 'Identificar médico'
  }
  return props.mode === 'change' ? 'Alterar médico' : 'Alocar médico'
})

// Com contrato e nenhuma linha aplicável (todas bloqueadas) não há o que enviar (M6-C).
const isSubmitBlocked = computed(
  () => coveragePlan.value.hasContract && coveragePlan.value.applicableRows.length === 0
)

// Sem `details` no 422 (backend antigo), vale a quantidade de plantões que a alocação aplicaria.
const preAllocationStationCount = computed(
  () =>
    qualificationGuard.preAllocation.affectedCount ||
    (coveragePlan.value.hasContract
      ? coveragePlan.value.applicableRows.length
      : shifts.value.length)
)

const submitLabel = computed(() => {
  // M4: com a prévia de cobertura o botão diz em quantos plantões vai aplicar.
  if (coveragePlan.value.hasContract) {
    const n = coveragePlan.value.applicableRows.length
    return n > 0 ? `Aplicar em ${pluralizeStations(n)}` : 'Aplicar'
  }
  if (isDoctorTarget.value) return 'Confirmar alocação'
  return needsTransitionConfirmation(coveragePlan.value) ? 'Continuar' : 'Confirmar'
})

const transitionContextLabel = computed(() => {
  const recurrenceTitle = recurrence.find((r) => r.value === selectedRecurrence.value)?.title
  const path = [
    props.stationDetails?.schedule?.healthUnit?.name,
    props.stationDetails?.schedule?.name,
    props.stationDetails?.position?.name
  ]
    .filter(Boolean)
    .join(' › ')
  return [recurrenceTitle, path].filter(Boolean).join(' · ')
})

// `initialCoverage` só vale se a escala permitir o modo pedido; senão abre em "Médico".
function resolveInitialCoverage(): CoverageMode {
  const requested = props.initialCoverage
  if (requested === CoverageMode.UNIDENTIFIED && allowUnidentified.value) return requested
  if (requested === CoverageMode.OPEN && scheduleSupports.value) return requested
  return CoverageMode.DOCTOR
}

const SUCCESS_MESSAGES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]: 'Alocação confirmada com sucesso!',
  [CoverageMode.UNIDENTIFIED]: 'Plantões marcados como ocupados sem identificação.',
  [CoverageMode.OPEN]: 'Vagas liberadas com sucesso.'
}

const {
  violations: consecutiveHoursViolations,
  hasViolations: hasConsecutiveHoursViolations,
  runCheck: runConsecutiveHoursCheck,
  applyErrorViolations: applyConsecutiveHoursError,
  clearViolations: clearConsecutiveHoursViolations
} = useConsecutiveHoursCheck()

watch([selectedDoctor, selectedTeam, shifts], () => clearConsecutiveHoursViolations())
watch(coverageMode, () => clearConsecutiveHoursViolations())

// Operação em massa: TODOS os plantões listados vão no mesmo lote — tudo-ou-nada.
function buildConsecutiveHoursCandidates(): ConsecutiveHoursCheckCandidate[] {
  const doctorId = selectedDoctor.value ? Number(selectedDoctor.value) : null
  const scheduleId = Number(props.stationDetails?.schedule?.id)
  if (!doctorId || !Number.isFinite(scheduleId)) return []

  // Com o contrato de cobertura, só os plantões que serão alterados entram na checagem:
  // linhas bloqueadas (IGNORED) não podem gerar falso positivo de horas consecutivas.
  const stations = coveragePlan.value.hasContract
    ? coveragePlan.value.applicableRows.map((row) => row.station)
    : shifts.value

  return stations.map((shift) => ({
    scheduleId,
    doctorId,
    stationId: shift.stationId,
    startHour: new Date(shift.startTime).toISOString(),
    endHour: new Date(shift.endTime).toISOString(),
    ourTeam: selectedTeam.value === 'internal',
    timezone: props.stationDetails?.timezone ?? '',
    excludeDoctorStationIds: shift.doctorStationId ? [shift.doctorStationId] : undefined
  }))
}

const { handleExpandTable, handleCloseExpandedTable } = useExpandedTable(
  isThisModalVisible,
  isExpandedTableModalOpen
)

const isLoading = computed(
  () => isLoadingDoctors.value || isLoadingFormations.value || isLoadingAllocators.value
)

const isViewShiftsButtonEnabled = computed(() => {
  if (coverageMode.value === CoverageMode.UNIDENTIFIED) return !!coverageResponsibleId.value
  if (coverageMode.value === CoverageMode.OPEN) return true
  return !!selectedDoctor.value && !!selectedTeam.value && !!selectedAllocator.value
})

const INDISPONIVEL = 'indisponível'

const computedDoctorDetails = computed(() => {
  const doctor = doctors.value.find((d) => d.value === selectedDoctor.value)
  const formation = formations.value.find((f) => f.value === selectedFormation.value)
  const team = teams.value.find((t) => t.value === selectedTeam.value)

  return {
    name: doctor ? doctor.title : 'Médico não selecionado',
    crm: doctor ? doctor.subtitle : INDISPONIVEL,
    formation: formation ? formation.title : INDISPONIVEL,
    team: team ? team.title : INDISPONIVEL
  }
})

const computedRecurrenceDescription = computed(() => {
  return (
    recurrenceDescriptions[selectedRecurrence.value as keyof typeof recurrenceDescriptions] || ''
  )
})

const initialPage = useInitialPage(toRef(props, 'stationDetails'))

const ALL_BLOCKED_MESSAGES: Record<CoverageMode, string> = {
  [CoverageMode.DOCTOR]:
    'Nenhum plantão pode ser alterado: todos os selecionados estão bloqueados.',
  [CoverageMode.UNIDENTIFIED]:
    'Nenhum plantão pode ser marcado como ocupado sem identificação: todos os selecionados estão bloqueados.',
  [CoverageMode.OPEN]:
    'Nenhum plantão pode ser liberado como vaga aberta: todos os selecionados estão bloqueados.'
}

const doctorNameToUse = computed(() => computedDoctorDetails.value.name)
const summaryText = computed(() => {
  if (!coveragePlan.value.hasContract) {
    return useSummaryText(doctorNameToUse, totalShiftsCount, 'management')
  }
  // Com contrato conta só as linhas aplicáveis (bloqueadas ficam de fora), como a prévia.
  const applicable = coveragePlan.value.applicableRows
  const n = applicable.length
  if (n === 0) return ALL_BLOCKED_MESSAGES[coverageMode.value]
  if (isDoctorTarget.value) return useSummaryText(doctorNameToUse, ref(n), 'management')
  if (coverageMode.value === CoverageMode.OPEN)
    return `Você está liberando ${pluralizeStations(n)} como vaga aberta. Deseja continuar?`
  // Já ocupado sem identificação + modo Ocupado = só troca o responsável ("Editar responsável").
  const swapping = applicable.filter((r) => r.status === CoverageStatus.UNIDENTIFIED).length
  const marking = n - swapping
  if (marking === 0)
    return `Você está trocando o responsável de ${pluralizeStations(swapping)}. Deseja continuar?`
  const markingText = `Você está marcando ${pluralizeStations(marking)} como ocupado sem identificação`
  return swapping > 0
    ? `${markingText} e trocando o responsável de ${pluralizeStations(swapping)}. Deseja continuar?`
    : `${markingText}. Deseja continuar?`
})

const formattedShifts = computed(() => {
  if (coveragePlan.value.hasContract) return coveragePlan.value.rows.map(toPreviewTableRow)
  return shifts.value.map((station) => ({
    id: `#${station.stationId}`,
    startDate: format(new Date(station.startDate), 'dd/MM/yyyy'),
    startTime: format(new Date(station.startTime), 'HH:mm'),
    endDate: format(new Date(station.endDate), 'dd/MM/yyyy'),
    endTime: format(new Date(station.endTime), 'HH:mm'),
    availability: formatLegacyAvailability(station),
    value: station.value ? `R$${station.value.toFixed(2).replace('.', ',')}` : INDISPONIVEL
  }))
})

const limitedFormattedShifts = computed(() => formattedShifts.value.slice(0, 3))

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const buildDoctorStationsFilters = (): ListStationsFromScheduleRequest | null => {
  if (!props.stationDetails?.schedule?.id) return null
  if (isDoctorTarget.value && !selectedDoctor.value) return null
  // Backend com a feature recebe o alvo da cobertura para calcular bloqueios do preview.
  const coverageFilter = scheduleSupports.value ? { coverage: coverageMode.value } : {}

  if (selectedRecurrence.value === 'unique') {
    const filters: ListStationsFromScheduleRequest = {
      schedule: props.stationDetails.schedule.id,
      station: props.stationDetails.id
    }

    if (isDoctorTarget.value) filters.doctor = Number(selectedDoctor.value)
    return { ...filters, ...coverageFilter }
  }

  const sDate = new Date(props.stationDetails.startHour)
  let eDate: Date | undefined

  if (selectedRepetition.value === 'personalized' && startDate.value) {
    eDate = endOfDay(startDate.value)
  }

  if (selectedRepetition.value === 'end_scale' && props.stationDetails.schedule.endDate) {
    eDate = new Date(props.stationDetails.schedule.endDate)
  }

  const weekDays = selectedDay.value.map((day) => dayMapping[day]).join(',')

  let weekOfMonth = '0,1,2,3,4,5'

  let fortnightlyFilters = {}

  if (selectedRecurrence.value === 'fortnightly') {
    fortnightlyFilters = {
      alternatingWeek: true,
      stationWeekParity: props.stationDetails.stationWeekParity
    }
  }

  if (selectedRecurrence.value === 'abcde') {
    weekOfMonth = selectedWeeks.value.map((week) => weekMapping[week]).join(',')
  }

  const filters: ListStationsFromScheduleRequest = {
    schedule: props.stationDetails.schedule.id,
    position: props.stationDetails?.position?.id,
    shifts: props.stationDetails?.shift?.id,
    startDate: sDate.toISOString(),
    weekDays: weekDays,
    weekOfMonth: weekOfMonth,
    ...fortnightlyFilters
  }

  if (eDate) {
    filters.endDate = eDate.toISOString()
  }

  if (isDoctorTarget.value && selectedDoctor.value) {
    filters.doctor = Number(selectedDoctor.value)
  }

  return { ...filters, ...coverageFilter }
}

const shouldShowInitialStationWarning = computed(() => {
  if (!props.stationDetails?.id) return false
  return !shifts.value.some((shift) => shift.stationId === props.stationDetails?.id)
})

const hasConflictingStations = computed(() => {
  return conflictShiftsCount.value > 0
})

/** `isStale`: o assistente descarta a resposta de uma listagem que ficou obsoleta em voo. */
async function handleViewDoctorStations(isStale?: () => boolean) {
  const baseFilters = buildDoctorStationsFilters()
  if (!baseFilters) {
    toast.error('Detalhes da escala não encontrados para buscar os plantões.')
    return
  }

  isLoadingShifts.value = true
  shifts.value = []
  try {
    const response = await stationService.listStationsFromSchedule({
      ...baseFilters,
      excludeHasCheckIn: true
    })
    if (isStale?.()) return

    shifts.value = response.stations
    totalShiftsCount.value = response.count
    openShiftsCount.value = response.countOpen
    closedShiftsCount.value = response.countClosed
    unregisteredShiftsCount.value = response.countUnregistered ?? null
    conflictShiftsCount.value = response.countConflict
    showShiftList.value = true
  } catch (error) {
    if (isStale?.()) return
    notifyError(error, 'Não foi possível carregar a lista de plantões')
    console.error(error)
  } finally {
    isLoadingShifts.value = false
  }
}

async function submitAllocation() {
  if (isDoctorTarget.value) {
    const isValidDoctor =
      !!selectedDoctor.value && doctors.value.some((d) => d.value === selectedDoctor.value)
    if (!isValidDoctor) {
      toast.error('Selecione um médico válido antes de confirmar a alocação.')
      return
    }
  } else if (coverageMode.value === CoverageMode.UNIDENTIFIED && !coverageResponsibleId.value) {
    toast.error('Selecione o responsável pela cobertura.')
    return
  }
  if (!validateReasons()) return

  // M6: médico real → sem identificação (ou lote todo bloqueado) passa pela confirmação.
  if (needsTransitionConfirmation(coveragePlan.value)) {
    isTransitionModalOpen.value = true
    return
  }
  await executeAllocation()
}

async function handleTransitionConfirm() {
  // M6 só fecha quando o lote foi aplicado; em falha fica aberto junto do toast de erro.
  const succeeded = await executeAllocation()
  if (succeeded) isTransitionModalOpen.value = false
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
async function executeAllocation(options: { preAllocate?: boolean } = {}): Promise<boolean> {
  isLoadingSubmit.value = true
  let succeeded = false
  try {
    const missingDoctorFields =
      isDoctorTarget.value &&
      (!selectedDoctor.value || !selectedTeam.value || !selectedAllocator.value)
    if (shifts.value.length === 0 || missingDoctorFields) {
      toast.error('Por favor, preencha todos os campos e visualize os plantões antes de confirmar.')
      return false
    }

    if (isDoctorTarget.value) {
      const canProceed = await runConsecutiveHoursCheck(buildConsecutiveHoursCandidates())
      if (!canProceed) {
        toast.error('Limite de horas consecutivas excedido. Nenhum plantão foi alterado.')
        return false
      }
    }

    const plan = coveragePlan.value
    const operations = buildCoverageOperations(plan, {
      fallbackOriginDoctorId: props.stationDetails?.doctorStation.doctor?.id ?? null,
      packageId: props.stationDetails?.doctorStation.package?.id ?? null
    })
    if (operations.length === 0) {
      toast.error('Nenhum plantão pode ser alterado com os parâmetros selecionados.')
      return false
    }

    const changeReasons = buildReasons({ originStationId: originStationId.value })
    const payload: PatchStationsFromSchedulePayload = {
      operations,
      patchStations: buildCoveragePatchStations({
        target: coverageMode.value,
        hasContract: plan.hasContract,
        doctorId: selectedDoctor.value ? Number(selectedDoctor.value) : null,
        ourTeam: selectedTeam.value === 'internal',
        userSponsorId: selectedAllocator.value,
        formationId: selectedFormation.value,
        stationType: selectedPeriodicity.value as StationTypes | null,
        responsibleId: coverageResponsibleId.value,
        note: coverageNote.value
      }),
      scheduleId: Number(props.stationDetails?.schedule.id),
      ...(options.preAllocate ? { preAllocate: true } : {}),
      ...(changeReasons ? { changeReasons } : {})
    }

    await stationService.patchStationsFromSchedule(payload)

    toast.success(SUCCESS_MESSAGES[coverageMode.value])
    emit('allocationUpdated')
    close()
    succeeded = true
    await stationStore.getListStations()
  } catch (error) {
    console.error('Erro ao submeter a alocação:', error)
    handleAllocationError(error)
  } finally {
    isLoadingSubmit.value = false
  }
  return succeeded
}

function handleAllocationError(error: unknown): void {
  // Qualificação exigida: bloqueio ou confirmação da pré-alocação (reenvia o lote com preAllocate).
  if (
    qualificationGuard.handleError(error, {
      doctorName: findDoctorTitle(doctors.value, selectedDoctor.value),
      retryWithPreAllocate: () => executeAllocation({ preAllocate: true })
    })
  ) {
    return
  }
  // Médico "coringa" (422 PLACEHOLDER_DOCTOR_BLOCKED): aviso descritivo em vez do toast.
  if (placeholderBlocked.handleError(error, findDoctorTitle(doctors.value, selectedDoctor.value))) {
    focusDoctorOnClose = false
    return
  }
  if (applyConsecutiveHoursError(error)) {
    toast.error('Limite de horas consecutivas excedido. Nenhum plantão foi alterado.')
    return
  }
  if (isPostStationApprovedError(error)) {
    // Defesa em profundidade (RN-07): a avaliação pode ter sido aprovada
    // enquanto o modal estava aberto — o backend recusa a mutação e devolve
    // o code fixo. Modal permanece aberto (sem close()), mesma decisão do
    // guard em update.station.modal.
    //
    // Sem sondagem aqui de propósito: este modal não tem estado de trava
    // preventiva (quem desabilita o acesso é o `stationDetailsModal`), então
    // usa só o helper de mensagem, não o composable.
    toast.error(getPostStationApprovedMessage(error))
    return
  }
  // Códigos de cobertura (Antecipe, check-in, flag desligada…) viram a frase do catálogo.
  const coverageMessage = getCoverageErrorMessage(error)
  if (coverageMessage) {
    toast.error(coverageMessage)
    return
  }
  // Antes: "Erro ao confirmar a alocação." estático.
  notifyError(error, 'Não foi possível confirmar a alocação')
}

const DOCTORS_LOAD_ERROR = 'Não foi possível carregar a lista de médicos'

/**
 * `useDoctorStore.fetchDoctors` não relança: a falha da lista só aparece em `isError`,
 * e o `catch` dos callers abaixo nunca recebe o erro da API. Sem isto o seletor de
 * médico ficava vazio sem aviso.
 */
function notifyDoctorsLoadFailure(): void {
  if (doctorStore.isError) notifyErrorMessage(DOCTORS_LOAD_ERROR)
}

const fetchDoctorsWithSearch = async (searchQuery: string) => {
  if (!props.stationDetails?.schedule?.healthUnit?.company?.id) return

  const companyId = props.stationDetails.schedule.healthUnit.company.id

  isLoadingDoctors.value = true
  try {
    await doctorStore.applyFilters(
      {
        inviteStatus: [
          CompanyDoctorInviteStatusEnum.ACCEPTED,
          CompanyDoctorInviteStatusEnum.PENDING
        ],
        companies: [String(companyId)],
        search: searchQuery
      },
      false
    )

    syncDoctorOptions()
    notifyDoctorsLoadFailure()
  } catch (error) {
    // A store não relança: este ramo só dispara com erro de código (a falha da API sai acima).
    notifyError(error, DOCTORS_LOAD_ERROR)
    console.error(error)
  } finally {
    isLoadingDoctors.value = false
  }
}

function handleSearchUpdate(search: string) {
  clearTimeout(searchDebounceTimer)
  // O Vuetify pode emitir `null` ao limpar o campo.
  lastDoctorSearch = search ?? ''
  isLoadingDoctors.value = true

  searchDebounceTimer = window.setTimeout(() => {
    fetchDoctorsWithSearch(search)
  }, 500)
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const fetchData = async () => {
  if (
    !props.stationDetails?.schedule?.id ||
    !props.stationDetails?.schedule?.healthUnit?.company?.id
  ) {
    return
  }

  let defaultDay = 'seg'
  let defaultWeek = 'A'

  if (props.stationDetails && props.stationDetails.startHour) {
    try {
      const date = new Date(props.stationDetails.startHour)
      if (!isNaN(date.getTime())) {
        const dayIndex = date.getDay()
        const foundKey = (Object.keys(dayMapping) as Array<keyof typeof dayMapping>).find(
          (key) => dayMapping[key] === dayIndex
        )
        if (foundKey) {
          defaultDay = String(foundKey)
        }

        const dayOfMonth = date.getDate()
        const weekIndex = Math.floor((dayOfMonth - 1) / 7)

        const weekLetters = ['A', 'B', 'C', 'D', 'E']
        if (weekIndex >= 0 && weekIndex < weekLetters.length) {
          defaultWeek = weekLetters[weekIndex]
        }
      }
    } catch (e) {
      // fallback intencional: erro local (parse de data), não de API — o formulário segue
      // com o padrão seg/semana A, visível nos seletores de dia e de semana.
      console.error('Erro ao calcular data padrão da station:', e)
    }
  }

  const scheduleId = props.stationDetails.schedule.id
  const companyId = props.stationDetails.schedule.healthUnit.company.id

  isLoadingDoctors.value = true
  try {
    await doctorStore.applyFilters(
      {
        inviteStatus: [
          CompanyDoctorInviteStatusEnum.ACCEPTED,
          CompanyDoctorInviteStatusEnum.PENDING
        ],
        companies: [String(companyId)],
        search: ''
      },
      false
    )
    syncDoctorOptions()
    notifyDoctorsLoadFailure()
  } catch (error) {
    // A store não relança: este ramo só dispara com erro de código (a falha da API sai acima).
    notifyError(error, DOCTORS_LOAD_ERROR)
    console.error(error)
  } finally {
    isLoadingDoctors.value = false
  }

  isLoadingFormations.value = true
  try {
    const formationRes = await new DsPaymentFormationService().getByScheduleId(scheduleId)
    formations.value = formationRes.data.map((item) => ({
      title: item.name,
      value: item.id
    }))
  } catch (error) {
    notifyError(error, 'Não foi possível carregar as formações')
    console.error(error)
  } finally {
    isLoadingFormations.value = false
  }

  isLoadingAllocators.value = true
  try {
    const allocatorRes = await new ScheduleUnitService().stationCreatingUsers(scheduleId)

    allocators.value = allocatorRes.map((user) => ({
      title: user.name,
      value: user.id
    }))
  } catch (error) {
    notifyError(error, 'Não foi possível carregar a lista de alocadores')
    console.error(error)
  } finally {
    isLoadingAllocators.value = false
  }

  if (props.mode === 'change' && props.stationDetails?.doctorStation) {
    const ds = props.stationDetails.doctorStation
    const incomingId = ds.doctor?.id ? String(ds.doctor.id) : null
    const isInList = !!incomingId && doctors.value.some((d) => d.value === incomingId)

    selectedDoctor.value = isInList ? incomingId : null

    if (incomingId && !isInList) {
      toast.warning(
        'Esta vaga não tem médico válido alocado. Selecione um médico para fazer a alocação.'
      )
    }

    selectedFormation.value = ds.dsPaymentDoctorFormation?.id || null
    selectedTeam.value = ds.ourTeam ? 'internal' : 'external'
    selectedAllocator.value = ds.userSponsor?.id || null
    selectedPeriodicity.value = ds.type
    selectedRecurrence.value = 'unique'
    selectedRepetition.value = 'end_scale'
    startDate.value = null
  }

  coverageMode.value = resolveInitialCoverage()
  coverageResponsibleId.value = null
  coverageNote.value = ''
  selectedResponsibleName.value = null
  if (coverageMode.value === CoverageMode.UNIDENTIFIED) {
    coverageResponsibleId.value = originCoverage.value.responsibleId
    coverageNote.value = originCoverage.value.note ?? ''
    selectedResponsibleName.value = originCoverage.value.responsibleName
  }

  selectedWeeks.value = [defaultWeek]
  selectedDay.value = [defaultDay]
}

function close() {
  // Convite criado: o pai só recarrega ao fechar (ver `submitInvite`).
  if (createdInvite.value) emit('allocationUpdated')
  emit('closeModal')
}

watch(
  () => props.stationDetails,
  (newDetails) => {
    if (newDetails && props.showModal) {
      fetchData()
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => props.showModal,
  (newValue) => {
    isThisModalVisible.value = newValue
    showShiftList.value = false
    shifts.value = []
    totalShiftsCount.value = 0
    openShiftsCount.value = 0
    closedShiftsCount.value = 0
    conflictShiftsCount.value = 0
    isTransitionModalOpen.value = false
    resetWizard()
    if (!newValue) {
      isExpandedTableModalOpen.value = false
      return
    }
    // RF4: toda abertura começa sem motivo, mesmo quando os detalhes ainda vão chegar.
    resetReasons()
    if (props.stationDetails) {
      selectedRepetition.value = 'end_scale'
      fetchData()
    }
  }
)
</script>
